import { PROMOCODE_CONFIG, PromocodeHelpers } from '../config/promocode.js';

class PromocodeGenerator {
  /**
   * Validate generation configuration
   * @param {object} config - Generation configuration
   * @returns {object} Validation result
   */
  static validateConfig(config) {
    const errors = [];
    
    if (!config.platform) {
      errors.push('Platform is required');
    }
    
    if (!config.planType) {
      errors.push('Plan type is required');
    }
    
    if (!config.credits || config.credits <= 0) {
      errors.push('Valid credits amount is required');
    }
    
    if (!config.expiryYear || config.expiryYear < new Date().getFullYear()) {
      errors.push('Valid expiry year is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate a new promocode with specified parameters
   * @param {object} config - Generation configuration
   * @returns {string} Generated promocode
   */
  static generate(config) {
    // Validate configuration first
    const validation = this.validateConfig(config);
    if (!validation.isValid) {
      throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
    }

    const {
      platform,
      planType,
      credits,
      expiryYear,
      customPrefix = ''
    } = config;

    // Only structured format is supported
    return this.generateStructuredPromocode({
      platform,
      planType,
      credits,
      expiryYear,
      customPrefix
    });
  }

  /**
   * Generate structured promocode: [PLATFORM][PLAN][CREDITS][EXPIRY][RANDOM]
   */
  static generateStructuredPromocode({ platform, planType, credits, expiryYear, customPrefix }) {
    const platformCode = this.getPlatformCode(platform);
    const planCode = this.getPlanCode(planType);
    const creditsCode = this.getCreditsCode(credits);
    const randomSuffix = this.generateRandomSuffix();

    return `${customPrefix}${platformCode}${planCode}${creditsCode}${expiryYear}${randomSuffix}`;
  }


  /**
   * Get platform code from platform name
   */
  static getPlatformCode(platform) {
    const platformConfig = PromocodeHelpers.getPlatformByName(platform);
    return platformConfig ? platformConfig.code : 'XX'; // Default to XX for unknown platforms
  }

  /**
   * Get plan code from plan type
   */
  static getPlanCode(planType) {
    const planCodes = {
      'lifetime': 'L',
      'monthly': 'M',
      'yearly': 'Y'
    };
    return planCodes[planType] || 'L';
  }

  /**
   * Get credits code with K/M suffix
   */
  static getCreditsCode(credits) {
    if (credits >= 1000000) {
      return `${credits / 1000000}M`;
    } else if (credits >= 1000) {
      return `${credits / 1000}K`;
    }
    return credits.toString();
  }

  /**
   * Generate random suffix for promocode
   */
  static generateRandomSuffix() {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  }

  /**
   * Generate multiple promocodes
   */
  static generateBulk(config, quantity = 1) {
    const codes = [];
    for (let i = 0; i < quantity; i++) {
      codes.push(this.generate(config));
    }
    return codes;
  }

  /**
   * Validate promocode format before generation
   */
  static validateConfig(config) {
    const errors = [];

    // Validate platform
    if (!PromocodeHelpers.getPlatformByName(config.platform)) {
      errors.push('Invalid platform');
    }

    // Validate plan type
    if (!PROMOCODE_CONFIG.planTypes[config.planType]) {
      errors.push('Invalid plan type');
    }

    // Validate credits
    if (config.credits < 100 || config.credits > 10000000) {
      errors.push('Credits must be between 100 and 10,000,000');
    }

    // Validate expiry year
    const currentYear = new Date().getFullYear();
    if (config.expiryYear < currentYear || config.expiryYear > currentYear + 10) {
      errors.push('Expiry year must be within next 10 years');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }
}

export default PromocodeGenerator;
